import { ComputedRef, Ref, computed, ref, watch } from 'vue';
import { WebOrder } from '@/types/webOrder';
import { fetchWebOrdersRaw } from './mintechAPI';
import { useFormatters } from '@/util/formatters';

export const useWebOrderManager = () => {
	const listModule = createListModule();
	const searchModule = createSearchModule(listModule.webOrdersRaw);
	const paginationModule = createPaginationModule(searchModule.searchedWebOrders);
	const tableModule = createTableModule(paginationModule.pagedWebOrders);
	const selectionModule = createSelectionModule(listModule.webOrdersRaw);
	const dataAccessModule = createDataAccessModule(listModule.webOrdersRaw);

	return {
		...listModule,
		...searchModule,
		...paginationModule,
		...tableModule,
		...selectionModule,
		...dataAccessModule
	};
};

//#region List
function createListModule() {
	const webOrdersRaw: Ref<WebOrder[]> = ref([]);

	return {
		webOrdersRaw
	};
}
//#endregion

//#region Search
function createSearchModule(webOrders: Ref<WebOrder[]>) {
	const searchQuery = ref<string>('');

	const searchedWebOrders: ComputedRef<WebOrder[]> = computed(() => {
		if (!searchQuery.value.trim()) return webOrders.value;
		const query = searchQuery.value.trim().toLowerCase();
		return webOrders.value.filter((webOrder) => {
			const customer = webOrder.company.toLowerCase() || webOrder.customerName.toLowerCase();
			const id = String(webOrder.id).toLowerCase();
			const total = webOrder.total.toLocaleString().toLowerCase();
			return customer.includes(query) || id.includes(query) || total.includes(query);
		});
	});

	const filteredWebOrdersCount: ComputedRef<number> = computed(() => searchedWebOrders.value.length);
	const filteredWebOrdersValue: ComputedRef<number> = computed(() =>
		searchedWebOrders.value.reduce((sum, webOrder) => sum + Number(webOrder.total), 0)
	);

	return {
		searchQuery,
		searchedWebOrders,
		filteredWebOrdersCount,
		filteredWebOrdersValue
	};
}
//#endregion

//#region Pagination
function createPaginationModule(webOrders: ComputedRef<WebOrder[]>) {
	const pageSize = ref<number>(15);
	const currentPage = ref<number>(1);
	const totalItems = computed<number>(() => webOrders.value.length);
	const totalPages = computed<number>(() => Math.max(1, Math.ceil(totalItems.value / pageSize.value)));
	const pageStartIndex = computed(() => (currentPage.value - 1) * pageSize.value);
	const pageEndIndex = computed(() => Math.min(pageStartIndex.value + pageSize.value, totalItems.value));
	const pagedWebOrders = computed(() => webOrders.value.slice(pageStartIndex.value, pageEndIndex.value));
	const isFirstPage = computed(() => currentPage.value === 1);
	const isLastPage = computed(() => currentPage.value === totalPages.value);

	function nextPage() {
		if (currentPage.value < totalPages.value) {
			currentPage.value++;
		}
	}

	function prevPage() {
		if (currentPage.value > 1) {
			currentPage.value--;
		}
	}

	function firstPage() {
		currentPage.value = 1;
	}

	function lastPage() {
		currentPage.value = totalPages.value;
	}

	watch(totalPages, (newVal) => {
		if (currentPage.value > newVal) {
			currentPage.value = newVal;
		}
	});

	return {
		currentPage,
		totalItems,
		totalPages,
		pageStartIndex,
		pageEndIndex,
		pagedWebOrders,
		isFirstPage,
		isLastPage,
		nextPage,
		prevPage,
		firstPage,
		lastPage
	};
}
//#endregion

//#region Table
function createTableModule(webOrders: ComputedRef<WebOrder[]>) {
	const tableHeadings: string[] = ['ID', 'Customer', 'Delivery', 'Payment', 'Total', 'Date'];

	const { formatDate, formatCurrency } = useFormatters();

	const rowData: ComputedRef<string[][]> = computed(() => {
		return webOrders.value.map((webOrder) => {
			const customer = webOrder.company ? webOrder.company : webOrder.customerName;
			return [webOrder.id, customer, webOrder.delivery, webOrder.payment, formatCurrency(webOrder.total), formatDate(webOrder.dateCreated)];
		});
	});

	return {
		tableHeadings,
		rowData
	};
}

//#endregion

//#region Selection
function createSelectionModule(webOrders: Ref<WebOrder[]>) {
	const selectedWebOrderId: Ref<string | null> = ref(null);

	const selectedWebOrder: ComputedRef<WebOrder | null> = computed(() => {
		if (!selectedWebOrderId.value) return null;
		return webOrders.value.find((wo) => wo.id === selectedWebOrderId.value) ?? null;
	});

	function selectWebOrder(id: string) {
		selectedWebOrderId.value = id;
	}

	function clearSelectedWebOrder() {
		selectedWebOrderId.value = null;
	}

	return {
		selectedWebOrderId,
		selectedWebOrder,
		selectWebOrder,
		clearSelectedWebOrder
	};
}

//#endregion

//#region Data Access
function createDataAccessModule(webOrders: Ref<WebOrder[]>) {
	async function fetchWebOrders() {
		try {
			webOrders.value = [];
			const webOrdersRaw = await fetchWebOrdersRaw();
			webOrders.value = webOrdersRaw.sort((a, b) => Number(b.id.replace(/^#/, '')) - Number(a.id.replace(/^#/, '')));
		} catch (e: any) {
			if (e instanceof Error) {
				throw new Error(`message: ${e.message}, cause: ${e.cause}`);
			}
			throw new Error('An unknown error occured when fetching web orders from the server');
		}
	}

	return { fetchWebOrders };
}

//#endregion
